// src/common/theme/palette.js
// ------------------------------------------------------------------
// Paleta Premium v3.0 consolidada (dark/light)
// Harmonização com Fleet Premium Design System
// Mantém compatibilidade com Revenda/Server
// ------------------------------------------------------------------

import {
 grey, green, red, orange, blue 
} from "@mui/material/colors";
import { DARK, LIGHT } from './colors';

// ------------------------------------------------------------------
// Validação de cor HEX
// ------------------------------------------------------------------
const validatedColor = (color) =>
  /^#([0-9A-Fa-f]{3}){1,2}$/.test(color) ? color : null;

// ------------------------------------------------------------------
// Escalas auxiliares (cinzas + institucional)
// ------------------------------------------------------------------
const tailWindGray = {
  50: "#f9fafb",
  100: "#f3f4f6",
  200: "#e5e7eb",
  300: "#d1d5db",
  400: "#9ca3af",
  500: "#6b7280",
  600: "#4b5563",
  700: "#374151",
  800: "#1f2937",
  900: "#111827",
};

const brandPrimaryScale = {
  50: "#FFF5F2",
  100: "#FFF1EE",
  200: "#FFE4DE",
  300: "#FFD5CC",
  400: "#FFBCAD",
  500: "#FE795D",
  600: "#EF562F",
  700: "#EB4F27",
  800: "#CC4522",
  900: "#A5371B",
};

// ------------------------------------------------------------------
// Bases refinadas
// ------------------------------------------------------------------
const BASE_LIGHT = {
  primary: {
    main: LIGHT.accent.primary,        // #00b894 (Teal)
    light: LIGHT.accent.muted,
    dark: LIGHT.accent.active,
    contrastText: LIGHT.text.inverse,
  },
  secondary: {
    main: LIGHT.semantic.info,         // #2563eb (Azul)
    contrastText: LIGHT.text.inverse,
  },
  background: {
    default: LIGHT.bg.base,            // #ffffff
    paper: LIGHT.bg.raised,            // #f6f8fa
  },
  text: {
    primary: LIGHT.text.primary,       // #1f2328
    secondary: LIGHT.text.secondary,   // #656d76
  },
};

const BASE_DARK = {
  primary: {
    main: DARK.accent.primary,         // #00d4aa (Teal)
    light: DARK.accent.hover,          // #00e4ba
    dark: DARK.accent.active,          // #00c49a
    contrastText: DARK.text.inverse,   // #0d1117
  },
  secondary: {
    main: DARK.semantic.info,          // #58a6ff (Azul)
    contrastText: DARK.text.inverse,
  },
  background: {
    default: DARK.bg.base,             // #0d1117
    paper: DARK.bg.raised,             // #161b22
  },
  text: {
    primary: DARK.text.primary,        // #f0f6fc
    secondary: DARK.text.secondary,    // #8b949e
  },
};

// ------------------------------------------------------------------
// Export principal
// ------------------------------------------------------------------
export default (server, revenda, darkMode) => {
  const tenant = revenda || server?.revenda || server?.attributes?.revenda || {};

  const primaryColor = validatedColor(
    tenant?.colors?.primary
      || tenant?.color1
      || server?.attributes?.colorPrimary
  );
  const secondaryColor = validatedColor(
    tenant?.colors?.secondary
      || tenant?.color2
      || server?.attributes?.colorSecondary
  );
  const successColor = validatedColor(
    tenant?.colors?.success
      || tenant?.color3
      || server?.attributes?.colorSuccess
  );

  const base = darkMode ? BASE_DARK : BASE_LIGHT;

  const primary = {
    ...base.primary,
    ...(primaryColor ? { main: primaryColor } : {}),
  };

  const secondary = {
    ...base.secondary,
    ...(secondaryColor ? { main: secondaryColor } : {}),
  };

  const palette = darkMode ? DARK : LIGHT;

  const success = {
    main: successColor || palette.semantic.success,
    light: palette.semantic.successBg,
    dark: palette.semantic.success,
    contrastText: darkMode ? DARK.text.inverse : '#fff',
  };

  const error = {
    main: palette.semantic.error,
    light: palette.semantic.errorBg,
    dark: palette.semantic.error,
    contrastText: '#fff',
  };

  const warning = {
    main: palette.semantic.warning,
    light: palette.semantic.warningBg,
    dark: palette.semantic.warning,
    contrastText: darkMode ? DARK.text.inverse : '#fff',
  };

  const info = {
    main: palette.semantic.info,
    light: palette.semantic.infoBg,
    dark: palette.semantic.info,
    contrastText: '#fff',
  };

  // Cores padrão para export (podem ser sobrescritas por tenant)
  const defaults = {
    csv: '#F97316',  csvHover: '#FB923C',
    excel: '#009688', excelHover: '#00796B',
    pdf: '#E53935',   pdfHover: '#C62828',
  };

  // 🔧 CORREÇÃO: construir a paleta de export como variável local
  const exportPalette = {
    csv:       tenant?.colors?.csv       || defaults.csv,
    csvHover:  tenant?.colors?.csvHover  || defaults.csvHover,
    excel:     tenant?.colors?.excel     || defaults.excel,
    excelHover:tenant?.colors?.excelHover|| defaults.excelHover,
    pdf:       tenant?.colors?.pdf       || defaults.pdf,
    pdfHover:  tenant?.colors?.pdfHover  || defaults.pdfHover,
  };

  return {
    mode: darkMode ? "dark" : "light",
    primary,
    secondary,
    success,
    error,
    warning,
    info,
    background: base.background,
    text: base.text,

    // --------------------------------------------------------------
    // Paletas auxiliares
    // --------------------------------------------------------------
    neutral: {
      main: grey[500],
      icon3d: brandPrimaryScale[400],
    },
    geometry: { main: "#3bb2d0" },
    alwaysDark: { main: grey[900] },

    // --------------------------------------------------------------
    // Customizações específicas
    // --------------------------------------------------------------
    // customizações usadas no cabeçalho/hover da tabela
    custom: {
      table: {
        headerBg:    darkMode ? palette.bg.elevated : LIGHT.bg.raised,
        headerBorder:darkMode ? palette.border.default : LIGHT.border.default,
        rowHover:    darkMode ? palette.bg.hover : LIGHT.bg.hover,
        headerText:  darkMode ? palette.text.secondary : LIGHT.text.primary,
      },
      // Cores de botões de ação
      button: {
        add:  { light: LIGHT.semantic.success, dark: DARK.semantic.success, textLight: '#fff', textDark: DARK.text.inverse },
        csv:  { light: '#F97316', dark: '#F97316', textLight: '#fff', textDark: '#fff' },
        xlsx: { light: '#009688', dark: '#009688', textLight: '#fff', textDark: '#fff' },
        pdf:  { light: LIGHT.semantic.error, dark: DARK.semantic.error, textLight: '#fff', textDark: '#fff' },
      },
    },

    // 🔌 exposto para os Botões CSV/Excel/PDF nos overrides
    export: exportPalette,
  };
};
